import { api } from "@/services/api";

/**
 * Store de subida de imágenes de eventos.
 *
 * URL estable: el backend devuelve una ruta relativa `/api/uploads/...`
 * y una `key`. El frontend guarda ambas para poder eliminar la anterior
 * en el reemplazo. Cuando se migre a Azure Blob la URL absoluta cambia
 * pero el contrato del store se mantiene.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const EXTENSIONES_PERMITIDAS = ["jpg", "jpeg", "png", "webp"];
export const MIME_ACCEPT = "image/jpeg,image/png,image/webp";

/**
 * Convierte una URL relativa (`/api/uploads/...`) o `key` a absoluta,
 * para poder pasarla a `<img src>`.
 */
export const urlImagenAbsoluta = (relOrKey) => {
  if (!relOrKey) return "";
  if (/^https?:\/\//i.test(relOrKey)) return relOrKey;
  if (relOrKey.startsWith("/api/uploads/")) return `${BACKEND_URL}${relOrKey}`;
  // Interpretamos como key sin prefix.
  return `${BACKEND_URL}/api/uploads/${relOrKey.replace(/^\//, "")}`;
};

const validarLocalmente = (file) => {
  if (!file) return "Selecciona un archivo.";
  const name = (file.name || "").toLowerCase();
  const ext = name.split(".").pop();
  if (!EXTENSIONES_PERMITIDAS.includes(ext)) {
    return "Formato no permitido. Usa JPG, PNG o WEBP.";
  }
  if (file.size > MAX_SIZE_BYTES) {
    return "La imagen supera 10 MB.";
  }
  return null;
};

/**
 * Sube la imagen al backend. Si se pasa `previousKey`, el backend
 * elimina la anterior automáticamente tras persistir la nueva.
 *
 * Devuelve `{ url, key, contentType, size }` en éxito o `{ error }`.
 */
export const subirImagenEvento = async (file, previousKey = "") => {
  const localErr = validarLocalmente(file);
  if (localErr) return { error: localErr };
  const form = new FormData();
  form.append("file", file);
  if (previousKey) form.append("previous_key", previousKey);
  try {
    const { data } = await api.post("/uploads/eventos/imagen", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });
    return data;
  } catch (err) {
    const detail = err?.response?.data?.detail;
    return { error: detail || "No se pudo subir la imagen." };
  }
};

/** Elimina una imagen conocida por `key`. Idempotente. */
export const eliminarImagenEvento = async (key) => {
  if (!key) return { ok: false };
  try {
    const { data } = await api.delete(
      `/uploads/eventos/imagen/${encodeURIComponent(key)}`,
    );
    return data;
  } catch {
    return { ok: false };
  }
};
